import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, Pencil, Trash2, X, Truck, BarChart3, Star } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'
import AmbassadorPerformanceModal from '../components/AmbassadorPerformanceModal'
import CsvControls from '../components/CsvControls'

interface Driver {
  id: string
  phone: string
  full_name: string
  active: number
  online: number
  created_at: string
  orders_delivered: number
  total_earnings_fils: number
  active_orders: number
  avg_rating: number | null
  rating_count: number
}

const formatJOD = (fils: number) => `JD ${(fils / 1000).toFixed(3)}`

const createSchema = z.object({
  full_name: z.string().min(1, 'Required'),
  phone:     z.string().min(7, 'Enter a valid phone number'),
  password:  z.string().min(8, 'At least 8 characters'),
})

const editSchema = z.object({
  full_name: z.string().min(1, 'Required'),
  active:    z.boolean(),
  password:  z.string().min(8, 'At least 8 characters').or(z.literal('')),
})

type CreateData = z.infer<typeof createSchema>
type EditData   = z.infer<typeof editSchema>

export default function Drivers() {
  const [modal, setModal] = useState<Driver | null | 'new'>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [perfDriverId, setPerfDriverId] = useState<string | null>(null)
  const qc = useQueryClient()

  const { data, isLoading } = useQuery<{ drivers: Driver[] }>({
    queryKey: ['drivers'],
    queryFn: () => api.get('/drivers').then(r => r.data),
  })

  const refresh = () => qc.invalidateQueries({ queryKey: ['drivers'] })

  async function handleDelete(driver: Driver) {
    if (!confirm(`Delete ${driver.full_name}? This cannot be undone.`)) return
    setDeleting(driver.id)
    try {
      await api.delete(`/drivers/${driver.id}`)
      refresh()
    } finally {
      setDeleting(null)
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-semibold text-white">Ambassadors</h1>
          <p className="text-xs text-gray-500 mt-0.5">Delivery ambassador accounts — credentials are set here and used in the DrinX Ambassador app</p>
        </div>
        <div className="flex items-center gap-2">
          <CsvControls
            basePath="/drivers"
            exportFilename={`drivers-export-${new Date().toISOString().slice(0, 10)}.csv`}
            exportOnly
          />
          <button
            onClick={() => setModal('new')}
            className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#a51830] text-white text-sm px-4 py-2 rounded-lg transition-colors"
          >
            <Plus size={15} /> Add Ambassador
          </button>
        </div>
      </div>

      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-600">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
                {['Ambassador', 'Phone', 'Status', 'Online', 'Active', 'Delivered', 'Rating', 'Earnings', 'Created', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.drivers.map(d => (
                <tr key={d.id} className={`border-b border-[#222] hover:bg-white/2 transition-colors ${!d.active ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-[#2a2a2a] border border-[#3a3a3a] flex items-center justify-center">
                        <Truck size={14} className="text-gray-500" />
                      </div>
                      <span className="text-white font-medium">{d.full_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs font-mono">{d.phone}</td>
                  <td className="px-4 py-3">
                    {d.active
                      ? <span className="text-xs bg-green-500/15 text-green-400 px-2 py-0.5 rounded-full">Active</span>
                      : <span className="text-xs bg-gray-500/15 text-gray-500 px-2 py-0.5 rounded-full">Inactive</span>
                    }
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <div className={`w-2 h-2 rounded-full ${d.online ? 'bg-green-400' : 'bg-gray-600'}`} />
                      <span className={`text-xs ${d.online ? 'text-green-400' : 'text-gray-600'}`}>
                        {d.online ? 'Online' : 'Offline'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {d.active_orders > 0
                      ? <span className="text-xs bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full">{d.active_orders} in progress</span>
                      : <span className="text-xs text-gray-600">—</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-white text-sm tabular-nums">{d.orders_delivered}</td>
                  <td className="px-4 py-3">
                    {d.avg_rating != null && d.rating_count > 0 ? (
                      <div className="flex items-center gap-1.5">
                        <Star size={12} className="text-amber-400 fill-amber-400" />
                        <span className="text-amber-400 text-sm font-semibold tabular-nums">
                          {d.avg_rating.toFixed(1)}
                        </span>
                        <span className="text-gray-600 text-[11px]">({d.rating_count})</span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-600">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-white text-sm tabular-nums">{formatJOD(d.total_earnings_fils)}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{formatDateTime(d.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setPerfDriverId(d.id)}
                        title="View performance"
                        className="text-gray-500 hover:text-[#C41E3A] transition-colors"
                      >
                        <BarChart3 size={14} />
                      </button>
                      <button onClick={() => setModal(d)} className="text-gray-500 hover:text-white transition-colors">
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(d)}
                        disabled={deleting === d.id}
                        className="text-gray-600 hover:text-red-400 transition-colors disabled:opacity-40"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!data?.drivers.length && (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-gray-600 text-sm">
                    No ambassadors yet — add the first one above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {modal !== null && (
        modal === 'new'
          ? <CreateModal onClose={() => setModal(null)} onSaved={() => { refresh(); setModal(null) }} />
          : <EditModal driver={modal} onClose={() => setModal(null)} onSaved={() => { refresh(); setModal(null) }} />
      )}

      {perfDriverId && (
        <AmbassadorPerformanceModal
          driverId={perfDriverId}
          onClose={() => setPerfDriverId(null)}
        />
      )}
    </div>
  )
}

function CreateModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<CreateData>({
    resolver: zodResolver(createSchema),
  })
  const [apiError, setApiError] = useState<string | null>(null)

  async function onSubmit(data: CreateData) {
    setApiError(null)
    try {
      await api.post('/drivers', data)
      onSaved()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      setApiError(err.response?.data?.message ?? 'Failed to create ambassador')
    }
  }

  return (
    <Modal title="Add Ambassador" onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Full Name</label>
          <input {...register('full_name')} autoFocus className={input} placeholder="Ahmad Al-Khalidi" />
          {errors.full_name && <p className="text-xs text-red-400 mt-1">{errors.full_name.message}</p>}
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Phone Number</label>
          <input {...register('phone')} type="tel" className={input} placeholder="+962791234567" />
          {errors.phone && <p className="text-xs text-red-400 mt-1">{errors.phone.message}</p>}
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Password</label>
          <input {...register('password')} type="password" className={input} placeholder="Min. 8 characters" />
          {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password.message}</p>}
        </div>
        {apiError && <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{apiError}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
          <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-50 text-white text-sm rounded-lg">
            {isSubmitting ? 'Creating…' : 'Create Ambassador'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function EditModal({ driver, onClose, onSaved }: { driver: Driver; onClose: () => void; onSaved: () => void }) {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<EditData>({
    resolver: zodResolver(editSchema),
    defaultValues: { full_name: driver.full_name, active: !!driver.active, password: '' },
  })
  const [apiError, setApiError] = useState<string | null>(null)

  async function onSubmit(data: EditData) {
    setApiError(null)
    try {
      const payload: Record<string, unknown> = { full_name: data.full_name, active: data.active }
      if (data.password) payload.password = data.password
      await api.patch(`/drivers/${driver.id}`, payload)
      onSaved()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      setApiError(err.response?.data?.message ?? 'Failed to update ambassador')
    }
  }

  return (
    <Modal title="Edit Ambassador" onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#2e2e2e]">
          <div className="w-10 h-10 rounded-full bg-[#2a2a2a] border border-[#3a3a3a] flex items-center justify-center">
            <Truck size={16} className="text-gray-500" />
          </div>
          <div>
            <p className="text-sm font-medium text-white">{driver.full_name}</p>
            <p className="text-xs text-gray-500 font-mono">{driver.phone}</p>
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Full Name</label>
          <input {...register('full_name')} className={input} />
          {errors.full_name && <p className="text-xs text-red-400 mt-1">{errors.full_name.message}</p>}
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">New Password <span className="text-gray-600">(leave blank to keep current)</span></label>
          <input {...register('password')} type="password" className={input} placeholder="Enter to reset password" />
          {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password.message}</p>}
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input {...register('active')} type="checkbox" className="w-4 h-4 accent-[#C41E3A]" />
          <span className="text-sm text-gray-300">Active (can log in)</span>
        </label>
        {apiError && <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{apiError}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
          <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-50 text-white text-sm rounded-lg">
            {isSubmitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e]">
          <h2 className="text-sm font-semibold text-white">{title}</h2>
          <button onClick={onClose}><X size={16} className="text-gray-400" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}

const input = 'w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A] placeholder:text-gray-600'
